import { v4 as uuid } from 'uuid';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from '../../services/localStorage';

export const NYL_TAB_ID_KEY = 'NYL_TAB_ID';
export const NYL_REG_FLOW_ID_KEY = 'NYL_REG_FLOW_ID';
export const NYL_REG_FLOW_CLAIM_KEY = 'NYL_REG_FLOW_CLAIM';
export const REGISTRATION_FLOW_CHANNEL = 'nyl-registration-flow';
export const REGISTRATION_FLOW_CLAIM_TTL_MS = 30 * 60 * 1000;

const isClaimForCurrentFlow = (claim) => {
  if (!claim || typeof claim.flowId !== 'string' || !claim.flowId) {
    return false;
  }
  const flowId = getRegistrationFlowId();
  return Boolean(flowId && claim.flowId === flowId);
};

const parseClaim = (raw) => {
  if (!raw) {
    return null;
  }
  try {
    const claim = JSON.parse(raw);
    if (
      !claim
      || typeof claim.tabId !== 'string'
      || typeof claim.timestamp !== 'number'
      || typeof claim.flowId !== 'string'
      || !claim.flowId
    ) {
      clearRegistrationFlowClaim();
      return null;
    }
    if (Date.now() - claim.timestamp > REGISTRATION_FLOW_CLAIM_TTL_MS) {
      clearRegistrationFlowClaim();
      return null;
    }
    return claim;
  } catch (e) {
    return null;
  }
};

export const getTabId = () => {
  try {
    let tabId = sessionStorage.getItem(NYL_TAB_ID_KEY);
    if (!tabId) {
      tabId = uuid();
      sessionStorage.setItem(NYL_TAB_ID_KEY, tabId);
    }
    return tabId;
  } catch (e) {
    return uuid();
  }
};

export const getRegistrationFlowId = () => {
  try {
    return getLocalStorageItem(NYL_REG_FLOW_ID_KEY) || null;
  } catch (e) {
    return null;
  }
};

export const startRegistrationFlow = () => {
  clearRegistrationFlowClaim();
  const flowId = uuid();
  try {
    setLocalStorageItem(NYL_REG_FLOW_ID_KEY, flowId);
  } catch (e) {
    // localStorage unavailable
  }
  return flowId;
};

export const isRegistrationFlowClaimed = () => {
  try {
    return parseClaim(getLocalStorageItem(NYL_REG_FLOW_CLAIM_KEY));
  } catch (e) {
    // localStorage unavailable (e.g. SecurityError in restricted contexts)
    return null;
  }
};

export const isOwnTabClaim = () => {
  const claim = isRegistrationFlowClaimed();
  if (!isClaimForCurrentFlow(claim)) {
    return false;
  }
  return claim.tabId === getTabId();
};

export const isClaimedByOtherTab = () => {
  const claim = isRegistrationFlowClaimed();
  if (!isClaimForCurrentFlow(claim)) {
    return false;
  }
  return claim.tabId !== getTabId();
};

const postClaimMessage = (tabId, flowId) => {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(REGISTRATION_FLOW_CHANNEL);
      channel.postMessage({
        type: 'claimed',
        tabId,
        flowId,
      });
      channel.close();
    }
  } catch (e) {
    // BroadcastChannel unavailable
  }
};

export const claimRegistrationFlow = () => {
  const tabId = getTabId();
  const flowId = getRegistrationFlowId();
  const claim = {
    tabId,
    timestamp: Date.now(),
    flowId: flowId || '',
  };
  try {
    setLocalStorageItem(NYL_REG_FLOW_CLAIM_KEY, JSON.stringify(claim));
  } catch (e) {
    // localStorage unavailable
  }
  if (flowId) {
    postClaimMessage(tabId, flowId);
  }
  return claim;
};

export const clearRegistrationFlowClaim = () => {
  try {
    removeLocalStorageItem(NYL_REG_FLOW_CLAIM_KEY);
  } catch (e) {
    // localStorage unavailable
  }
};

export const clearRegistrationFlow = () => {
  clearRegistrationFlowClaim();
  try {
    removeLocalStorageItem(NYL_REG_FLOW_ID_KEY);
  } catch (e) {
    // localStorage unavailable
  }
};

export const subscribeToRegistrationFlowClaim = (callback) => {
  const handlers = [];

  const handleClaim = (tabId, flowId) => {
    if (!tabId || tabId === getTabId()) {
      return;
    }
    const currentFlowId = getRegistrationFlowId();
    if (!flowId || !currentFlowId || flowId !== currentFlowId) {
      return;
    }
    try {
      callback({
        type: 'claimed',
        tabId,
        flowId,
      });
    } catch (e) {
      // Fail safely when subscriber callbacks throw
    }
  };

  let channel;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(REGISTRATION_FLOW_CHANNEL);
      const onMessage = (event) => {
        if (event.data && event.data.type === 'claimed') {
          handleClaim(event.data.tabId, event.data.flowId);
        }
      };
      channel.addEventListener('message', onMessage);
      handlers.push(() => channel.removeEventListener('message', onMessage));
    }
  } catch (e) {
    // BroadcastChannel unavailable
  }

  const onStorage = (event) => {
    if (event.key === NYL_REG_FLOW_CLAIM_KEY && event.newValue) {
      try {
        const claim = JSON.parse(event.newValue);
        handleClaim(claim.tabId, claim.flowId);
      } catch (err) {
        // ignore malformed claim
      }
    }
  };

  try {
    window.addEventListener('storage', onStorage);
    handlers.push(() => window.removeEventListener('storage', onStorage));
  } catch (e) {
    // ignore
  }

  return () => {
    handlers.forEach((unsub) => unsub());
    if (channel) {
      try {
        channel.close();
      } catch (e) {
        // ignore
      }
    }
  };
};
